import { useState } from "react"
import { type Control } from "react-hook-form"

import CustomerSelect from "@components/customer_select"
import { PRIMARY } from "@constants/colors"
import useTranslate from "@hooks/use_translate"
import FilterListIcon from "@mui/icons-material/FilterList"
import {
  Badge,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import { CustomerPerson } from "__generated__/graphql"

import {
  OPERATOR_PERIODS,
  OPERATOR_SHOW,
  OperatorPeriod,
  OperatorShow
} from "./operatorRun.utils"

type Props = {
  control: Control<{ customerId: string }>
  customerId: string
  persons: CustomerPerson[]
  cPersonId: string
  onPerson: (cPersonId: string) => void
  period: OperatorPeriod
  onPeriod: (period: OperatorPeriod) => void
  show: OperatorShow
  onShow: (show: OperatorShow) => void
}

export const personName = (person: CustomerPerson) =>
  [person.firstName, person.lastName].filter(Boolean).join(" ") ||
  person.email ||
  person._id

function Field({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Stack gap={0.5}>
      <InputLabel>
        <Typography>{label}</Typography>
      </InputLabel>
      {children}
    </Stack>
  )
}

export default function OperatorFilterPopover(props: Props) {
  const { translate } = useTranslate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const active = [
    props.customerId,
    props.cPersonId,
    props.show,
    props.period !== "24h"
  ].filter(Boolean).length

  return (
    <>
      <Tooltip title={translate("filter")}>
        <IconButton
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ color: anchorEl ? PRIMARY : "inherit" }}
        >
          <Badge badgeContent={active} color="primary">
            <FilterListIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 280, p: 1.5 } } }}
      >
        <Stack gap={1.25}>
          <CustomerSelect
            control={props.control}
            name="customerId"
            label={translate("ai_operator_customer")}
          />
          <Field label={translate("ai_operator_user")}>
            <Select
              size="small"
              displayEmpty
              disabled={!props.customerId}
              value={props.cPersonId}
              onChange={(event) => props.onPerson(event.target.value)}
            >
              <MenuItem value="">
                {translate("ai_operator_person_all")}
              </MenuItem>
              {props.persons.map((person) => (
                <MenuItem key={person._id} value={person._id}>
                  {personName(person)}
                </MenuItem>
              ))}
            </Select>
          </Field>
          <Field label={translate("ai_operator_time")}>
            <Select
              size="small"
              value={props.period}
              onChange={(event) =>
                props.onPeriod(event.target.value as OperatorPeriod)
              }
            >
              {OPERATOR_PERIODS.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(`ai_operator_period_${value}`)}
                </MenuItem>
              ))}
            </Select>
          </Field>
          <Field label={translate("ai_operator_status")}>
            <Select
              size="small"
              displayEmpty
              value={props.show}
              onChange={(event) =>
                props.onShow(event.target.value as OperatorShow)
              }
            >
              <MenuItem value="">{translate("ai_operator_show_all")}</MenuItem>
              {OPERATOR_SHOW.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(
                    value === "FAILED"
                      ? "ai_operator_status_failed"
                      : `ai_operator_outcome_${value}`
                  )}
                </MenuItem>
              ))}
            </Select>
          </Field>
        </Stack>
      </Popover>
    </>
  )
}
